/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { PropTypes } from 'prop-types'
import Index from '../Index'
import { Toggle } from '../Toggle'
import TicketLogo from './TicketLogo'
import TicketBanner from './TicketBanner'
import { ContentHeader } from '../ContentHeader'
import { LabeledGridRow } from './LabeledGridRow'
import ContentLabel from '../ContentLabel'
import { QRCode } from 'Elevate/adapters/presenters/react/QRCode/QRCode'
import { AddToAppleWallet } from '../AddToAppleWallet'
import { ticketHeaderRow } from './TicketFront.style'
import { Grid } from 'Elevate/adapters/presenters/react/Grid'
import { Mask } from 'Elevate/adapters/presenters/react/Mask'
import { TransferStatusModal } from '../TicketTransfer/TransferStatusModal'
import Transfer, { PENDING, ACCEPTED } from '../../../../entities/Transfer'

const VALID_TRANSFER_STATUSES = [PENDING, ACCEPTED]

const TicketFront = ({
  id,
  credentialName,
  eventName,
  venueName,
  eventPresenter,
  eventDates,
  seating,
  barcode,
  apiUrl,
  ticketIndex,
  totalTickets,
  setShowBack,
  showBack,
  isIOSDevice,
  orderReference,
  images = {},
  isTransferable,
  transfer = {},
}) => {
  return (
    <div>
      {renderTransferStatusModal(transfer, orderReference)}
      <Grid columns={1} divided>
        <TicketLogo src={images.logo} />
        <TicketBanner src={images.strip} />
        <Grid.Row css={ticketHeaderRow}>
          <Grid.Column textAlign="left">
            <ContentHeader>{credentialName}</ContentHeader>
          </Grid.Column>
        </Grid.Row>
        {seating ? <Seating {...seating} /> : null}
        <LabeledGridRow label="Event">
          {`${eventName} at ${venueName}`}
          {eventPresenter ? ` presented by ${eventPresenter}` : null}
        </LabeledGridRow>
        <LabeledGridRow label="Event Dates">{eventDates}</LabeledGridRow>
        <CenterLayout>
          <QRCode barcode={barcode} />
        </CenterLayout>
        {isIOSDevice && (
          <LabeledGridRow textAlign="center">
            <AddToAppleWallet ticketId={id} apiUrl={apiUrl} />
          </LabeledGridRow>
        )}
      </Grid>
      <TicketControls
        {...{
          ticketIndex,
          totalTickets,
          setShowBack,
          showBack,
          orderReference,
          id,
          isTransferable,
        }}
      />
    </div>
  )
}

const Seating = ({ section, row, seat }) => {
  const columnWidth = 5
  return (
    <Grid.Row>
      <Grid.Column textAlign="left" width={columnWidth}>
        <ContentLabel>Section</ContentLabel>
        {section}
      </Grid.Column>
      <Grid.Column textAlign="center" width={columnWidth + 1}>
        <ContentLabel>Row</ContentLabel>
        {row}
      </Grid.Column>
      <Grid.Column textAlign="right" width={columnWidth}>
        <ContentLabel>Seat</ContentLabel>
        {seat}
      </Grid.Column>
    </Grid.Row>
  )
}

const CenterLayout = props => (
  <div
    css={css`
      margin: 0 auto;
      text-align: center;
    `}
    {...props}
  />
)

const TicketControls = ({
  ticketIndex,
  totalTickets,
  setShowBack,
  showBack,
  orderReference,
  id,
  isTransferable,
}) => (
  <Grid>
    <Grid.Row>
      <Grid.Column width={5} align="left">
        <Index currentIndex={ticketIndex} total={totalTickets} />
      </Grid.Column>
      <Grid.Column width={6} align="center">
        {isTransferable && (
          <a href={`/orders/${orderReference}/tickets/transfer/${id}`}>
            Transfer
          </a>
        )}
      </Grid.Column>
      <Grid.Column width={5} align="right">
        <Toggle
          handleToggle={() => setShowBack(!showBack)}
          toggleState={showBack ? 'up' : 'down'}
        />
      </Grid.Column>
    </Grid.Row>
  </Grid>
)

const renderTransferStatusModal = (transfer, orderReference) =>
  isTicketInTransferStatus(transfer) ? (
    <Mask>
      <TransferStatusModal
        recipient={transfer.recipient}
        orderReference={orderReference}
        status={transfer.status}
        metadata={transfer.metadata}
      />
    </Mask>
  ) : null

const isTicketInTransferStatus = transfer => {
  return VALID_TRANSFER_STATUSES.includes(transfer.status)
}

export default TicketFront

TicketFront.propTypes = {
  id: PropTypes.string,
  credentialName: PropTypes.string,
  eventName: PropTypes.string,
  eventPresenter: PropTypes.string,
  eventDates: PropTypes.string,
  venueName: PropTypes.string,
  seating: PropTypes.object,
  barcode: PropTypes.string,
  apiUrl: PropTypes.string,
  images: PropTypes.object,
  isTransferable: PropTypes.bool,
  transfer: PropTypes.instanceOf(Transfer),
}
